import type { AtlasPin, Thread, ThreadListQuery } from '@atlas/api-contract';

import type { ViewerPanelFilters } from '@/features/viewer/viewer-store';

import {
  authorInitial,
  threadReplyCount,
  threadScreenMatches,
} from './thread-display';

/**
 * The one derivation that makes the panel filters mean something.
 *
 * ============================================================================
 * WHY THE FILTER IS APPLIED TWICE
 * ============================================================================
 * `toThreadListQuery` sends the filters to the API, which is what keeps the
 * response small and the `[versionId, resolvedAt]` index doing the work. But
 * the *canvas* is filtered here as well, from the same `filters` object, and
 * that is not redundant:
 *
 * - Between a filter change and the refetch landing, React Query hands back the
 *   previous response. Re-filtering locally means the pins change the instant
 *   the checkbox does, instead of a beat later.
 * - "Only this screen" cannot be expressed as an API filter without lying. The
 *   server compares `screenPath` exactly; the agent — and therefore this app —
 *   treats a pin with no hash as belonging to the screen whatever hash the
 *   prototype has since jumped to (spec R3.4). Asking the server for
 *   `screenPath=/settings.html` while standing on `/settings.html#billing`
 *   returns nothing, and the reviewer would watch their own pins vanish.
 *
 * So the query is a *narrowing* — never the whole story — and the visible set
 * is always computed from the list actually in hand.
 */

export interface ThreadSelectionInput {
  readonly threads: readonly Thread[];
  readonly filters: ViewerPanelFilters;
  /** The screen on display, in the agent's spelling. `null` until it reports. */
  readonly currentScreenPath: string | null;
  /**
   * In-document screen identity of the screen on display, for single-URL apps.
   * `null` for classic prototypes and until the agent reports; a thread with a
   * non-null `screenKey` only matches when this equals it.
   */
  readonly currentScreenKey: string | null;
}

/**
 * What to ask the API for.
 *
 * `screenPath` is deliberately absent even when "only this screen" is on: see
 * the note above. Status is sent, because `open` is the default view and
 * fetching every resolved thread of a mature version to throw them away would
 * be the one filter that genuinely costs a round trip.
 */
export const toThreadListQuery = (
  filters: ViewerPanelFilters,
): ThreadListQuery => ({ status: filters.status });

/** Whether a thread survives the status filter. */
const matchesStatus = (thread: Thread, filters: ViewerPanelFilters): boolean => {
  switch (filters.status) {
    case 'open':
      return thread.resolvedAt === null;
    case 'resolved':
      return thread.resolvedAt !== null;
    default:
      return true;
  }
};

/**
 * The rows the panel lists, newest first.
 *
 * The API already returns newest-first and the order is preserved rather than
 * re-sorted: `createdAt` has second-level ties in a burst of comments, and the
 * server breaks them consistently with its own secondary key.
 */
export const selectPanelThreads = ({
  threads,
  filters,
  currentScreenPath,
  currentScreenKey,
}: ThreadSelectionInput): Thread[] =>
  threads.filter(
    (thread) =>
      matchesStatus(thread, filters) &&
      (!filters.currentScreenOnly ||
        threadScreenMatches(
          thread.screenPath,
          currentScreenPath,
          thread.screenKey,
          currentScreenKey,
        )),
  );

/**
 * The threads whose pins the agent should draw.
 *
 * Always the panel's rows narrowed to the current screen, never more: a pin the
 * panel is hiding must not be sitting on the canvas, or "only this screen"
 * would filter a list while leaving the thing the list describes untouched.
 *
 * Until the agent has reported a screen the set is empty rather than
 * everything. Guessing would draw another screen's pins over this one for the
 * few hundred milliseconds before `atlas:ready` arrives, and a pin that appears
 * and then jumps is worse than a pin that appears late.
 */
export const selectPinThreads = (input: ThreadSelectionInput): Thread[] => {
  if (input.currentScreenPath === null) {
    return [];
  }

  return selectPanelThreads(input).filter((thread) =>
    threadScreenMatches(
      thread.screenPath,
      input.currentScreenPath,
      thread.screenKey,
      input.currentScreenKey,
    ),
  );
};

/** A thread reduced to what the agent needs in order to draw its marker. */
export const toAtlasPin = (thread: Thread): AtlasPin => ({
  threadId: thread.id,
  screenPath: thread.screenPath,
  screenKey: thread.screenKey,
  selector: thread.selector,
  offsetX: thread.offsetX,
  offsetY: thread.offsetY,
  fallbackX: thread.fallbackX,
  fallbackY: thread.fallbackY,
  viewportWidth: thread.viewportWidth,
  initial: authorInitial(thread.createdByName),
  replyCount: threadReplyCount(thread),
  resolved: thread.resolvedAt !== null,
});

export const toAtlasPins = (threads: readonly Thread[]): AtlasPin[] =>
  threads.map(toAtlasPin);

/**
 * The `showResolved` flag on `atlas:render-pins`.
 *
 * Belt and braces with the filtering above — the pin set already excludes
 * resolved threads under the default filter — but the agent draws whatever it
 * is given, and a resolved thread that arrives from a refetch a frame before
 * the next `render-pins` would otherwise flash onto the canvas.
 */
export const shouldShowResolvedPins = (filters: ViewerPanelFilters): boolean =>
  filters.status !== 'open';

/**
 * Removes a thread from a cached list.
 *
 * Used for exactly one thing: `DELETE /v1/comments/:id` answering
 * `threadDeleted: true`. The response is proof the thread is gone, so applying
 * it to the cache before the refetch lands is not an optimistic update — it is
 * the server's own answer, and it is what makes the pin disappear on the same
 * frame the confirmation dialog closes rather than a round trip later.
 */
export const removeThread = (
  threads: readonly Thread[] | undefined,
  threadId: string,
): Thread[] | undefined =>
  threads === undefined
    ? undefined
    : threads.filter((thread) => thread.id !== threadId);

/**
 * Which thread the popover should be showing after a comment was deleted.
 *
 * `null` closes it. Deleting the opening comment takes the thread with it, so
 * there is nothing left to keep open; deleting a reply leaves the thread
 * exactly where it was, and closing the popover would throw away the reviewer's
 * place in a conversation they are still reading.
 */
export const nextSelectedThreadId = (
  selectedThreadId: string | null,
  result: { readonly threadDeleted: boolean; readonly threadId: string },
): string | null =>
  result.threadDeleted && selectedThreadId === result.threadId
    ? null
    : selectedThreadId;
